import { useEffect, useState } from "react"
import Table from "../../components/Table/Table"
import {
  GridCallbackDetails,
  GridColDef,
  GridRowParams,
  GridRowsProp,
  GridSortModel,
} from "@mui/x-data-grid"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { setRowSelectionModel } from "../../slices/transactionsSlice"
import dayjs from "dayjs"
import { INDIAN_STANDARD_DATE_TIME } from "../../constants/format"
import {
  CategoriesState,
  PaginationType,
  selectCategories,
  setCategoryPagination,
} from "../../slices/categorySlice"

interface CategoryTableProps {
  category: keyof CategoriesState
  columns: GridColDef[]
  sortModel?: GridSortModel | undefined
  onSortModelChange?:
    | ((model: GridSortModel, details: GridCallbackDetails) => void)
    | undefined
  fetchTransaction?: () => void
}

export const CategoryTable = ({
  category,
  sortModel,
  onSortModelChange,
  columns,
}: CategoryTableProps) => {
  const { categoryList, pageInfo, isCategoryLoading, pagination } =
    useAppSelector((state) => selectCategories(state)[category])

  const dispatch = useAppDispatch()
  const [rowCountState, setRowCountState] = useState(
    pageInfo?.totalRowCount || 0,
  )

  useEffect(() => {
    setRowCountState((prevRowCountState: number) =>
      pageInfo?.totalRowCount !== undefined
        ? pageInfo?.totalRowCount
        : prevRowCountState,
    )
  }, [pageInfo?.totalRowCount, setRowCountState])

  const rows: GridRowsProp = categoryList?.map((category, index) => ({
    rowIndex: index,
    id: index,
    // id: category.level3Id ?? category.level2Id ?? category.level1Id ?? index,
    level1Id: category.level1Id,
    level1Name: category.level1Name,
    level1Status: category.level1Status,

    level2Id: category.level2Id,
    level2Name: category.level2Name,
    level2Status: category.level2Status,

    level3Id: category.level3Id,
    level3Name: category.level3Name,
    level3Status: category.level3Status,

    source: category.source ?? "-",
    createdAt: dayjs(category.createdAt).format(INDIAN_STANDARD_DATE_TIME),
    createdTimestamp: category.createdTimestamp,
    updatedAt: category.updatedAt
      ? dayjs(category.updatedAt).format(INDIAN_STANDARD_DATE_TIME)
      : "-",
  }))

  const columnVisibilityModel = {
    id: false,
    branch: false,
    balance: false,
    valueDate: false,
    paymentId: false,
  }

  const setPaginationModel = (page: PaginationType) => {
    dispatch(setCategoryPagination({ category, pagination: page }))
  }

  return (
    <>
      <Table
        rows={rows}
        columns={columns}
        loading={isCategoryLoading}
        rowCount={rowCountState}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationModel={pagination}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        disableColumnFilter
        columnVisibilityModel={columnVisibilityModel}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        disableRowSelectionOnClick
        onRowSelectionModelChange={(newRowSelectionModel) => {
          dispatch(
            setRowSelectionModel({
              rowSelectionModel: newRowSelectionModel as string[],
            }),
          )
        }}
        isRowSelectable={(params: GridRowParams) => !params.row.categoryList}
      />
    </>
  )
}
